import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "@ds/lib/utils";
import { buildGrid, HEATMAP_WEEKS, type HeatmapDay } from "../heatmap.model";

/**
 * A YEAR OF TRAINING, ONE CELL PER DAY.
 *
 * ═══ WHAT IT IS FOR, AND WHY IT IS NOT THE SAME AS THE LINE CHARTS ═══
 *
 * Every other chart in this product answers "how much" or "how heavy". This one answers
 * CONSISTENCY — did you show up, how often, and how the good weeks sit against the quiet ones.
 * A year of that is a shape you read in a second and cannot get from a line: a fortnight off
 * over Christmas is four blank columns, and no amount of staring at a volume curve says the same
 * thing as fast.
 *
 * ═══ 🚨 THE INTENSITY IS RELATIVE TO THE ATHLETE, NOT TO A FIXED SCALE ═══
 *
 * `thresholdsFor` splits their own trained days into quartiles, so all four shades are always in
 * use. A fixed scale would tell somebody doing three sets a day that they never train, and paint
 * somebody doing thirty a solid block from March onwards. The comparison this chart makes is
 * always their days against their own — see the model, which argues it in full.
 *
 * ═══ WHY THERE IS NO YEAR PICKER, WHICH THE REFERENCE DESIGN HAS ═══
 *
 * ⚠️ IT SHOWS A ROLLING WINDOW ENDING TODAY, ALWAYS — see {@link HEATMAP_WEEKS} for how long and
 * why. A rolling window is the more useful of the two: it always contains today, so the chart is
 * about the training an athlete is actually in the middle of rather than a calendar year that is
 * three weeks old in January. A picker would also need the endpoint to answer arbitrary years,
 * which the range vocabulary (`3m` · `1y` · `3y` · `all`) does not express. Said out loud rather
 * than left as a gap.
 *
 * ═══ 🚨 AND IT OPENS ON THE END, NOT THE BEGINNING ═══
 *
 * Whatever is left to scroll, the athlete lands on the week they are IN. A grid that overflows
 * its box starts at scroll position zero, which is the OLDEST column — so a chart whose whole
 * subject is "have I been training lately" opened on the part of the window that answers that
 * least, and the current week was reachable only by a sideways drag nothing advertises.
 */

/** Monday first, and only three of the seven are labelled — see the render. */
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export interface HeatmapProps {
  /**
   * What happened on each day, keyed `YYYY-MM-DD`. A day that is absent is a day with no
   * training, which is a real answer and by far the most common one.
   */
  values: ReadonlyMap<string, number>;
  /** What a screen reader is told instead of the grid. */
  label: string;
  /** Row labels, Monday first — the caller translates them. */
  weekdayLabels: readonly string[];
  /** Month names, January first — the caller translates them. */
  monthLabels: readonly string[];
  /** How one day is worded when it is tapped: "12 sets · 4'080 kg". */
  describe: (day: HeatmapDay) => string;
  /** The two ends of the legend. */
  lessLabel: string;
  moreLabel: string;
  className?: string;
}

/** The five steps. Zero is its own colour and not the palest shade of the ramp — see the model. */
const LEVEL_CLASS = [
  "bg-muted",
  "bg-accent/25",
  "bg-accent/45",
  "bg-accent/70",
  "bg-accent",
] as const;

export function Heatmap({
  values,
  label,
  weekdayLabels,
  monthLabels,
  describe,
  lessLabel,
  moreLabel,
  className,
}: HeatmapProps) {
  const grid = useMemo(() => buildGrid(values, HEATMAP_WEEKS), [values]);

  /**
   * SCROLLED TO TODAY, ONCE, BEFORE THE FIRST PAINT.
   *
   * `useLayoutEffect` rather than `useEffect` so the grid is never painted at the far left and
   * then jumped — on a slower phone that jump is visible and reads as the chart loading twice.
   *
   * ⚠️ ON MOUNT ONLY, DELIBERATELY. Re-anchoring whenever `values` changed would yank an athlete
   * back to the present the moment a background refetch landed, in the middle of them dragging
   * back through their own history. Both callers render a skeleton until their data has arrived,
   * so this component only ever mounts already populated — there is no later fill to follow.
   */
  const scroller = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    /* Clamped by the browser, so this is "as far right as it goes" rather than a computed
       offset that has to be kept in step with the cell size. */
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, []);
  /**
   * WHICH DAY IS BEING READ.
   *
   * ⚠️ A TAP, NOT A HOVER. On the device this ships to there is no pointer, so a hover-only
   * readout is a feature that does not exist — and a 10px cell needs a way to say what it is
   * that does not depend on being able to hit it precisely twice.
   */
  const [reading, setReading] = useState<HeatmapDay | null>(null);

  return (
    <figure className={cn("flex flex-col gap-2", className)}>
      {/*
        THE GRID SCROLLS SIDEWAYS INSIDE ITS OWN BOX, and on a narrow phone it still may need
        to: shrinking the cells until everything fits makes each one smaller than a fingertip on
        a control you tap to read. `scroll-area` is this project's own class for a region that
        scrolls without dragging the page with it. Whatever is left over, the effect above puts
        the current week in view first.
      */}
      <div ref={scroller} className="scroll-area overflow-x-auto">
        <div className="flex min-w-max gap-1.5">
          {/* THE WEEKDAY RAIL. Only alternate rows are labelled: seven words down the side of a
              grid this size is more type than grid, and Mon/Wed/Fri is enough to orient by. */}
          <div className="flex shrink-0 flex-col gap-0.5 pt-4">
            {WEEKDAYS.map((weekday) => (
              <span
                key={weekday}
                className="text-eyebrow text-muted-foreground flex h-3 items-center"
              >
                {weekday % 2 === 0 ? weekdayLabels[weekday] : ""}
              </span>
            ))}
          </div>

          <div className="flex flex-col gap-0.5">
            {/* THE MONTH RAIL, positioned by column so a label sits over the week it names. */}
            <div className="relative h-4">
              {grid.months.map((month) => (
                <span
                  key={`${month.column}-${month.month}`}
                  className="text-eyebrow text-muted-foreground absolute top-0"
                  style={{ left: `${month.column * 14}px` }}
                >
                  {monthLabels[month.month]}
                </span>
              ))}
            </div>

            <div
              role="img"
              aria-label={label}
              data-testid="heatmap"
              data-heatmap-weeks={grid.weeks.length}
              className="flex gap-0.5"
            >
              {grid.weeks.map((week) => (
                <div key={week.start} className="flex flex-col gap-0.5">
                  {week.days.map((day) => (
                    <button
                      key={day.day}
                      type="button"
                      data-testid="heatmap-day"
                      data-heatmap-level={day.inRange ? day.level : "future"}
                      aria-label={describe(day)}
                      /* A DAY THAT HAS NOT HAPPENED IS A HOLE, never a rest day the athlete has
                         already failed to fill. It is also not tappable — there is nothing to
                         say about next Thursday. */
                      disabled={!day.inRange}
                      onClick={() => setReading((current) => (current?.day === day.day ? null : day))}
                      className={cn(
                        "size-3 rounded-inline",
                        day.inRange ? LEVEL_CLASS[day.level] : "bg-transparent",
                        reading?.day === day.day && "ring-foreground ring-1",
                      )}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/*
        THE READOUT AND THE LEGEND SHARE A ROW, and the readout wins it when there is one. Two
        stacked lines under a chart this short is more chrome than chart, and a legend is only
        needed until the athlete has read one cell.
      */}
      <figcaption className="text-eyebrow text-muted-foreground flex min-h-4 items-center justify-between gap-3">
        <span data-testid="heatmap-reading" className="truncate tabular-nums">
          {reading === null ? "" : describe(reading)}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {lessLabel}
          {LEVEL_CLASS.map((level, index) => (
            <span key={index} className={cn("size-3 rounded-inline", level)} />
          ))}
          {moreLabel}
        </span>
      </figcaption>
    </figure>
  );
}
